import {inject, Injectable} from '@angular/core';
import {ModelSectionsService} from '../core/model-sections.service';
import {UiToolsService} from '../core/ui-tools.service';
import {SectionsContainer} from '../core/definitions/sections-container';
import {SuperObjectModel} from '../core/definitions/super-object-model';
import {ToolbarAction} from '../core/definitions/toolbar-action';
import {OperationActionType} from '../core/definitions/operation-action-type.enum';
import {OperationService} from './operation.service';
import {OperationStepType} from '../core/definitions/operation-step-type.enum';
import {OperationContainer} from '../core/definitions/operation-container';
import {OperationStepExecutionResult} from '../core/definitions/operation-step-execution-result';
import {LoggerService} from "../core/logger.service";

export interface RunActionResult {
  registerHasBeenRun: boolean;
  operationStepExecutionResult: OperationStepExecutionResult;
}

@Injectable({
  providedIn: 'root'
})
export class OperationToolbarServiceService {
  // Injections
  private readonly loggerService: LoggerService = inject(LoggerService);
  private readonly modelSectionsService: ModelSectionsService = inject(ModelSectionsService);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly uiToolsService: UiToolsService = inject(UiToolsService);

  private readonly invalidObjectReason = 'TRANS__OPERATION__DISABLED_REASON__INVALID_OBJECT';
  private readonly objectInUseReason = 'TRANS__DELETE_OBJECT__DISABLE_REASON__OBJECTS_IN_USE';
  private readonly noValidContextObjectsReason = 'TRANS__OPERATION__DISABLE_REASON__NO_VALID_CONTEXT_OBJECTS';
  private reportedKeys = [];

  getSectionsContainers(operationContainer: OperationContainer): SectionsContainer[] {
    return this.operationService.getCurrentOperationSectionContainers(operationContainer);
  }

  getOperationObjects(operationContainer: OperationContainer): SuperObjectModel[] {
    return this.getSectionsContainers(operationContainer).map(sectionsContainer => sectionsContainer.rootObject);
  }

  getIsInvalid(operationContainer: OperationContainer, registerHasBeenRun: boolean): boolean {
    let res = false;
    if (registerHasBeenRun) {
      const sectionsContainers = this.getSectionsContainers(operationContainer);
      if (sectionsContainers) {
        for (const sectionsContainer of sectionsContainers) {
          if (sectionsContainer.formGroup?.invalid) {
            res = true;
            break;
          }
        }
      } else {
        res = true;
      }
    }
    return res;
  }

  getTooltipText(selectorEnabled, action) {
    if (action.$$disabledReason) {
      return action.$$disabledReason;
    }
    if (selectorEnabled && action.helper_box_text_open_selector) {
      return action.helper_box_text_open_selector;
    }
    if (!selectorEnabled && action.helper_box_text) {
      return action.helper_box_text;
    }
  }

  isDisabled(operationContainer: OperationContainer, action: ToolbarAction, registerHasBeenRun: boolean): boolean {
    let res = false;
    if (this.isDisabledForMediaContainers(operationContainer)) {
      return true;
    }
    const sectionsContainers = this.getSectionsContainers(operationContainer);
    const sectionsContainer = sectionsContainers ? sectionsContainers[0] : null;
    switch (operationContainer.currentOperation.$$currentStep.operation_step_type) {
      case OperationStepType.EDIT_OBJECT:
      case OperationStepType.CONTINUE_NOT_SUBMITTED_OBJECT_GET_OPERATION_CONTEXTS:
      case OperationStepType.CREATE_OBJECT:
        res = this.isDisabledForEditObject(operationContainer, sectionsContainers, action, registerHasBeenRun);
        break;
      case OperationStepType.EDIT_SELECTED_COPY:
        res = this.isDisabledForCopyOperation(operationContainer);
        break;
      case OperationStepType.EDIT_NEW_OBJECT:
      case OperationStepType.DELETE_FROM_OBJECT:
      case OperationStepType.SELECT_MODEL_FOR_NEXT_STEP:
      case OperationStepType.CREATE_OBJECT_FROM_MODEL_IN_PREV_STEP:
      case OperationStepType.CONTINUE_NOT_SUBMITTED_OBJECT:
      case OperationStepType.CONTINUE_SUBMITTED_OBJECT:
      case OperationStepType.CHANGE_STATE:
      case OperationStepType.COPY_OBJECT:
      case OperationStepType.GET_EDIT_OBJECT:
        // Do nothing for now
        break;
      case OperationStepType.UPLOAD_MEDIA:
        res = this.isDisabledForUploadOperation(operationContainer);
        break;
      case OperationStepType.DELETE_OBJECT:
        // The below test may be used on more operation step types
        if (sectionsContainer?.operationContextObjects) {
          res = !sectionsContainer.operationContextObjects.length;
          // Disable register button if any objects are in use in other objects
          if (!res && sectionsContainer.rootObject.object_usage?.length) {
            const hasUsage = sectionsContainer.rootObject.object_usage.find(usage => usage.objectsUsing.length);
            res = !!hasUsage;
            action.$$disabledReason = this.objectInUseReason;
          } else if (action.$$disabledReason === this.objectInUseReason) {
            action.$$disabledReason = '';
          }
        }
        break;
      default:
        this.loggerService.info(`No disable check implemented for ${operationContainer.currentOperation.$$currentStep.operation_step_type}`)
    }
    return res;
  }

  getErrorCount(operationContainer: OperationContainer) {
    let errCount = 0;
    const sectionsContainers = this.getSectionsContainers(operationContainer);
    if (sectionsContainers) {
      for (const sectionsContainer of sectionsContainers) {
        errCount += this.modelSectionsService.getSectionErrors(sectionsContainer).length;
      }

    }
    return errCount;
  }

  nextError(operationContainer: OperationContainer) {
    // TODO: Rewrite in order to support multiple sections!!!
    const errorId = this.modelSectionsService.nextError(this.getSectionsContainers(operationContainer)[0]);
    if (!this.uiToolsService.scrollTo(errorId)) {
      this.uiToolsService.scrollToOpenArrayItem(errorId, 0, this.getOperationObjects(operationContainer)[0]);
    }
  }

  clearScrollTimeout() {
    this.uiToolsService.clearScrollToArrayItemTimeout();
  }

  async runAction(operationContainer: OperationContainer,
                  action: ToolbarAction,
                  useCmsErrorHandler: boolean,
                  lastOperationStepExecutionResult?: OperationStepExecutionResult,
  ): Promise<RunActionResult> {
    let registerHasBeenRun = false;
    let operationStepExecutionResult: OperationStepExecutionResult;
    switch (action.action_type) {
      case OperationActionType.REGISTER:
      case OperationActionType.DELETE:
      case OperationActionType.REGISTER_STAY_ON_STEP:
        operationStepExecutionResult = await this.executeOperation(operationContainer, action, useCmsErrorHandler);
        registerHasBeenRun = true;
        break;
      case OperationActionType.CANCEL_REGISTER:
        await this.operationService.cancelOperationStep(operationContainer, action);
        break;
      case OperationActionType.CLOSE_NO_SUBMIT:
        operationStepExecutionResult = await this.operationService.closeOperationStep(operationContainer);
        break;
      case OperationActionType.NEXT_STEP:
        this.operationService.goNextOperationStep(operationContainer);
        break;
      case OperationActionType.PREVIOUS_STEP:
        this.operationService.goPrevOperationStep(operationContainer);
        break;
      case OperationActionType.CHANGE_STATE:
        await this.operationService.changeState(operationContainer.currentOperation.$$currentStep, lastOperationStepExecutionResult);
        await this.operationService.cancelOperationStep(operationContainer, action);
        break;
      case OperationActionType.CLIENT_EXECUTE:
        this.loggerService.info('Client execute action triggered');
        await this.operationService.clientExecuteOperationStep(operationContainer);
        break;
      default:
        console.warn('Unable to handle action type ' + action.action_type);
    }
    return {
      operationStepExecutionResult: operationStepExecutionResult,
      registerHasBeenRun: registerHasBeenRun
    };
  }

  private async executeOperation(operationContainer: OperationContainer,
                                 action: ToolbarAction,
                                 useCmsErrorHandler: boolean): Promise<OperationStepExecutionResult> {
    let canRun = true;
    let res: OperationStepExecutionResult;
    const sectionsContainers = this.getSectionsContainers(operationContainer);
    if (sectionsContainers) {
      for (const sectionsContainer of sectionsContainers) {
        sectionsContainer.submitted = true;
        if (sectionsContainer.formGroup?.invalid) {
          canRun = false;
        }
      }
    }
    if (canRun) {
      // Execute operation towards server
      res = await this.operationService.executeOperationStep(operationContainer, null, action, useCmsErrorHandler);
    }
    return res;
  }

  private isDisabledForEditObject(operationContainer: OperationContainer,
                                  sectionsContainers: SectionsContainer[],
                                  action: ToolbarAction,
                                  registerHasBeenRun: boolean): boolean {
    let res = false;
    if (action.action_type === OperationActionType.REGISTER ||
      action.action_type === OperationActionType.REGISTER_STAY_ON_STEP ||
      action.action_type === OperationActionType.NEXT_STEP) {
      if (!sectionsContainers) {
        return true;
      }
      if (sectionsContainers[0].rootObject.operation_contexts_restricted?.length) {
        if (sectionsContainers[0].rootObject.operation_contexts?.length) {
          action.$$disabledReason = '';
        } else {
          res = true;
          action.$$disabledReason = this.noValidContextObjectsReason;
        }
      }
      if (res) {
        return res;
      }
      res = this.checkDirtyCheck(operationContainer, sectionsContainers, action, registerHasBeenRun);
      if (!res) {
        res = this.checkHasAnnotateImage(operationContainer);
      }
    }
    return res;
  }

  /**
   * Return boolean value representing if its completed or not.
   * @param operationContainer
   */
  isCompleted(operationContainer: OperationContainer) {
    return operationContainer.currentOperation.$$currentStep.operation_step_type === OperationStepType.CHANGE_STATE;
  }

  private checkDirtyCheck(operationContainer: OperationContainer,
                          sectionsContainers: SectionsContainer[],
                          action: ToolbarAction,
                          registerHasBeenRun: boolean) {
    let res = false;
    if (operationContainer.currentOperation.$$currentStep.skip_dirty_check) {
      return false;
    }
    const formGroups = sectionsContainers.map(sectionsContainer => sectionsContainer.formGroup);
    for (const formGroup of formGroups) {
      if (!formGroup) {
        continue;
      }
      res = !formGroup.dirty;
      if (res) {
        break;
      }
      if ([OperationActionType.REGISTER.toString(),
        OperationActionType.REGISTER_STAY_ON_STEP.toString()].includes(action.action_type)) {
        res = registerHasBeenRun && formGroup.invalid;
      } else {
        res = formGroup.invalid;
      }
      if (res) {
        action.$$disabledReason = this.invalidObjectReason;
        if (formGroup.invalid) {
          for (const key of Object.keys(formGroup.controls)) {
            if (formGroup.controls[key].invalid && !this.reportedKeys.includes(key)) {
              console.log(`Invalid control: ${key}`, formGroup.controls[key]);
              this.reportedKeys.push(key);
            }
          }
        }
      } else if (action.$$disabledReason === this.invalidObjectReason) {
        action.$$disabledReason = '';
      }
      if (res) {
        break;
      }
    }
    return res;
  }

  checkHasAnnotateImage(operationContainer: OperationContainer) {
    let res = false
    const operationObjects = this.getOperationObjects(operationContainer);
    for (const operationObject of operationObjects) {
      if (operationObject.$$annotateImage) {
        res = true;
        break;
      }
    }
    return res;
  }

  private isDisabledForMediaContainers(operationContainer: OperationContainer) {
    let res = false;
    if (operationContainer.currentOperation.$$operationUploadContainer?.uploadMediaContainers) {
      const uploadMediaContainers = operationContainer.currentOperation.$$operationUploadContainer?.uploadMediaContainers || [];
      for (const uploadMediaContainer of uploadMediaContainers) {
        if (uploadMediaContainer.changesOccurred ||
          uploadMediaContainer.fileObjectSectionsContainers.length !== uploadMediaContainer.files.length) {
          res = true;
          break;
        }
      }
    }
    return res;
  }

  private isDisabledForUploadOperation(operationContainer: OperationContainer) {
    let res: boolean;
    const uploadMediaContainers = operationContainer.currentOperation?.$$operationUploadContainer?.uploadMediaContainers;
    if (uploadMediaContainers) {
      res = !uploadMediaContainers[0].files?.length;
    } else {
      res = false;
    }
    return res;
  }

  private isDisabledForCopyOperation(operationContainer: OperationContainer) {
    // Check if any fields are flagged `keep`
    const sectionContainer = this.getSectionsContainers(operationContainer)[0];
    const sections = sectionContainer?.sections ?? [];
    return !sections.some(section => section.editFields.some(field => field.keep === true));
  }
}
